import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "../../lib/api";
import { wabaApi } from "../../lib/wabaApi";
import { SavedNote } from "../shared/Icons";
import { toastError } from "../shared/toast";
import { ModelSelector, type ModelSelectorValue } from "../agent/ModelSelector";
import { ModelFallbackSection, fallbackToForm, formToFallback } from "../agent/ModelFallbackSection";

// Leads de portal por e-mail. Duas partes, dois backends:
// - Remetentes (waba): 1ª barreira — só e-mail de remetente cadastrado vai pro core; o nome vira badge/source.
// - Modelo (core): o MESMO seletor do agente (ConfigModelo.tsx) + fallback, salvo em system_settings
//   portais_*; cada extração vira execução `portal` com steps `llm` (tokens, custo, resposta da tool).

type Sender = { name: string; match: string };
type ModelCfg = {
  model_provider: string; model_name: string; temperature: number; reasoning_effort: string | null;
  verbosity: string | null; max_tokens: number; model_fallback: Record<string, unknown> | null;
};
type Attempt = {
  status: string; model: string; provider: string; tokens_in: number; tokens_out: number; cost_usd: number;
  tool_call: { name: string; arguments: Record<string, unknown> } | null; error: string | null;
};
type TestOut = {
  resultado: Record<string, unknown> | null; lead: { nome: string; telefone: string; email: string };
  erro: string | null; tentativas: Attempt[]; entraria: boolean;
};

const inputStyle: React.CSSProperties = {
  padding: "8px 12px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 8,
  color: "var(--ink)", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box",
};
const cardStyle: React.CSSProperties = { border: "1px solid var(--line)", borderRadius: 10, padding: 16, display: "flex", flexDirection: "column", gap: 12 };
const h2Style: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: "var(--ink)", margin: 0 };
const muted: React.CSSProperties = { fontSize: 11, color: "var(--ink-muted)", lineHeight: 1.5 };
const btn = (primary: boolean): React.CSSProperties => ({
  padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
  border: primary ? "none" : "1px solid var(--line-strong)",
  background: primary ? "var(--brand)" : "transparent", color: primary ? "var(--surface-card)" : "var(--ink)",
});
const fmtUsd = (v: number) => (v >= 0.01 ? v.toFixed(2) : v.toFixed(5));

export function EmailPortaisTab() {
  const qc = useQueryClient();
  // Sem refetch por foco: os useEffect copiam `data` pro formulário; um refetch ao voltar
  // pra aba apagaria o que está sendo digitado. Recarrega só depois de salvar.
  const senders_q = useQuery<{ senders: Sender[] }>({
    queryKey: ["email-portais-senders"], queryFn: () => wabaApi.get("/dev/email-portais"),
    staleTime: Infinity, refetchOnWindowFocus: false,
  });
  const model_q = useQuery<ModelCfg>({
    queryKey: ["portais-model"], queryFn: () => api.get("/dev/portais/config"),
    staleTime: Infinity, refetchOnWindowFocus: false,
  });

  const [senders, setSenders] = useState<Sender[]>([]);
  useEffect(() => { if (senders_q.data) setSenders(senders_q.data.senders); }, [senders_q.data]);

  const [model, setModel] = useState<ModelSelectorValue>({
    provider: "", model_name: "", temperature: 0.1, reasoning_effort: "off", verbosity: "default", maxTokens: 1024,
  });
  const [fallback, setFallback] = useState<ModelSelectorValue | null>(null);
  useEffect(() => {
    const d = model_q.data;
    if (!d) return;
    setModel({
      provider: d.model_provider, model_name: d.model_name, temperature: d.temperature,
      reasoning_effort: d.reasoning_effort ?? "off", verbosity: d.verbosity ?? "default", maxTokens: d.max_tokens,
    });
    setFallback(fallbackToForm(d.model_fallback as any));
  }, [model_q.data]);

  const saveSenders = useMutation({
    mutationFn: () => wabaApi.put("/dev/email-portais", { senders: senders.filter((s) => s.name.trim() && s.match.trim()) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["email-portais-senders"] }),
    onError: (e: any) => toastError(e?.payload?.detail ?? "Erro ao salvar remetentes"),
  });
  const saveModel = useMutation({
    mutationFn: () => api.patch("/dev/portais/config", {
      model_provider: model.provider, model_name: model.model_name, temperature: model.temperature,
      reasoning_effort: model.reasoning_effort === "off" ? null : model.reasoning_effort,
      verbosity: model.verbosity === "default" ? null : model.verbosity,
      max_tokens: model.maxTokens ?? 1024, model_fallback: formToFallback(fallback),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portais-model"] }),
    onError: (e: any) => toastError(e?.payload?.detail ?? "Erro ao salvar o modelo"),
  });

  const [testSubject, setTestSubject] = useState("");
  const [testText, setTestText] = useState("");
  const [testOut, setTestOut] = useState<TestOut | string | null>(null);
  const test = useMutation({
    mutationFn: () => api.post<TestOut>("/dev/portais/testar", { subject: testSubject, text: testText }),
    onSuccess: (r) => setTestOut(r),
    onError: (e: any) => setTestOut(e?.payload?.detail ?? String(e)),
  });

  const setSender = (i: number, patch: Partial<Sender>) =>
    setSenders(senders.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  return (
    <div style={{ maxWidth: 820, display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={muted}>
        E-mails que chegam em <code>*@mail.revendai.com</code> viram evento v3 (canal e-mail) sempre. Só os de
        remetentes cadastrados aqui vão pro agente vinculado à conexão: o modelo abaixo extrai o comprador
        (execução "Portal (e-mail)" na aba Execuções, com tokens, custo e a resposta da tool), o lead sai no
        canal portais e o agente chama por template (aba Portais do agente). Sem remetente ou sem modelo, nada roda.
      </div>

      <div style={cardStyle}>
        <h2 style={h2Style}>Remetentes dos portais</h2>
        <span style={muted}>Nome = o que aparece como badge/<code>source</code>. Casa com = e-mail exato (<code>leads@icarros.com.br</code>) ou domínio (<code>socarrao.com.br</code>, inclui subdomínios).</span>
        {senders.map((s, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr auto", gap: 8, alignItems: "center" }}>
            <input style={inputStyle} placeholder="Nome (ex.: Só Carrão)" value={s.name} onChange={(e) => setSender(i, { name: e.target.value })} />
            <input style={inputStyle} placeholder="e-mail ou domínio" value={s.match} onChange={(e) => setSender(i, { match: e.target.value })} />
            <button onClick={() => setSenders(senders.filter((_, j) => j !== i))} style={{ ...btn(false), color: "var(--danger)" }}>Remover</button>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => setSenders([...senders, { name: "", match: "" }])} style={btn(false)}>+ Remetente</button>
          <button onClick={() => saveSenders.mutate()} disabled={saveSenders.isPending} style={btn(true)}>{saveSenders.isPending ? "Salvando…" : "Salvar remetentes"}</button>
          <SavedNote show={saveSenders.isSuccess} />
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={h2Style}>Modelo de extração</h2>
        <span style={muted}>Mesmo seletor do agente; chaves são as de Modelos Disponíveis / Chaves de API. Tool forçada (<code>registrar_lead</code>): principal falhou (erro, timeout, sem a tool) → fallback, uma vez.</span>
        <ModelSelector value={model} onChange={setModel} showVerbosity showMaxTokens />
        <ModelFallbackSection showVerbosity value={fallback} onChange={setFallback}
          hint="Usado quando o modelo principal falha na extração." />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => saveModel.mutate()} disabled={saveModel.isPending || !model.model_name} style={btn(true)}>{saveModel.isPending ? "Salvando…" : "Salvar modelo"}</button>
          <SavedNote show={saveModel.isSuccess} />
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={h2Style}>Testar extração</h2>
        <span style={muted}>Cole o assunto e o corpo de um e-mail real de portal. Roda a config salva (principal → fallback) e mostra cada chamada: modelo, tokens, custo e a resposta da tool.</span>
        <input style={inputStyle} placeholder="Assunto" value={testSubject} onChange={(e) => setTestSubject(e.target.value)} />
        <textarea style={{ ...inputStyle, height: 140, resize: "vertical", lineHeight: 1.5 }} placeholder="Corpo do e-mail" value={testText} onChange={(e) => setTestText(e.target.value)} />
        <button onClick={() => test.mutate()} disabled={test.isPending || !testText.trim()} style={{ ...btn(false), alignSelf: "flex-start" }}>
          {test.isPending ? "Extraindo…" : "Extrair"}
        </button>
        {typeof testOut === "string" && <span style={{ fontSize: 12, color: "var(--danger)" }}>{testOut}</span>}
        {testOut && typeof testOut !== "string" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12 }}>
            {testOut.tentativas.map((a, i) => (
              <div key={i} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--line)", background: "var(--surface-input)" }}>
                <div style={{ color: a.status === "ok" ? "var(--success)" : "var(--danger)", fontWeight: 600 }}>
                  {a.status === "ok" ? "OK" : "Falhou"} · {a.provider}/{a.model} · {a.tokens_in} → {a.tokens_out} tk · ${fmtUsd(a.cost_usd)}
                </div>
                {a.error && <div style={{ color: "var(--danger)" }}>{a.error}</div>}
                {a.tool_call && <pre style={{ margin: "6px 0 0", fontSize: 11, whiteSpace: "pre-wrap" }}>{JSON.stringify(a.tool_call.arguments, null, 2)}</pre>}
              </div>
            ))}
            <div style={{ color: "var(--ink)" }}>
              <b>Lead:</b> {testOut.lead.nome || "—"} · {testOut.lead.telefone || "sem telefone"} · {testOut.lead.email || "sem e-mail"} ·{" "}
              <b style={{ color: testOut.entraria ? "var(--success)" : "var(--ink-muted)" }}>{testOut.entraria ? "entraria como lead" : "não entraria"}</b>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
